import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
} from '@material-ui/core';
import styles from './SecondaryTable.module.scss';
import { v4 as uuid } from 'uuid';
import {
  AddIcon,
  ExpandIcon,
  CountryIcon,
  PlatformIcon,
  DateIcon,
  GraphIcon,
  TableIcon,
} from 'app/assets/images/icons';
import DotsMenu from 'app/components/DotsMenu';
import SelectInput from '../../Form/Inputs/SelectInput';
import { _t } from 'app/utils/i18n';
import { getFromLocalStorage } from 'app/utils/localStorage';
import { loadAllCountries, loadSecondaryTableData } from 'app/Requests';
import { Link, useLocation } from 'react-router-dom';
import Loader from 'app/components/Loader';
import { stackedChartTransformer } from 'app/utils/chartsTransformer';
import NoData from 'app/components/NoData';
import CardTitle from 'app/components/CardTitle';
import { useQuery, useQueryClient } from 'react-query';
import { path } from 'app/utils/path';
import StackedColumnChart from 'app/components/StackedColumnChart/StackedColumnChart';
import { handelNameKey } from 'app/utils/handel-name-key';
import { Grid } from '@material-ui/core';
import ServerError from 'app/components/ErrorBoundary/ServerError';
import { navigateWithSearchState } from 'app/utils/search-state';
import { useAppSettings } from 'app/context/settings/appSettings';

interface Props {
  compId: string;
  moduleName?: string;
  columns?: {
    name?: string;
    title?: string;
    header?: string | JSX.Element;
    render?: (data: any, row?: any) => any;
    defaultFlex?: number;
    resizable?: boolean;
    flex?: number;
    key?: string;
    sortable?: boolean;
    minWidth?: number;
    defaultWidth?: number;
    maxWidth?: number;
    defaultLocked?: string;
    columnStyle?: any;
  }[];
  title?: string;
  rowAction?: (id: number, rowData: any) => void;
  hideFilters?: boolean;
  hidePlatform?: boolean;
  hideToggleView?: boolean;
  hideCountry?: boolean;
  expandUrl?: string | { pathname: string; state: any };
  data?: any;
  customFilters?: any;
  timeIntervalFilter?: boolean;
  tableCardTitle?: string;
  expandAction?: any;
  seeAllLink?: any;
  currentModule?: string;
  hideDots?: boolean;
  notPlatforms?: boolean;
  distributed?: boolean;
  notClickable?: boolean;
  firstTitle?: string;
  secondTitle?: string;
  expandToTopCountries?: boolean;
  customTooltip?: any;
  trending_topics?: any;
  grid?: any;
  hideIfNoData?: boolean;
  maxRows?: number;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  allCountriesFilter?: boolean;
  expandUrlWithNavigate?: boolean;
}

const direction = getFromLocalStorage('LNG') == 'ar' ? 'right' : 'left';

type TGD = {
  categories: Array<string>;
  series: Array<any>;
};

const SecondaryTable: FunctionComponent<Props> = ({
  tableCardTitle,
  moduleName,
  columns = [],
  rowAction,
  expandUrl,
  hideFilters,
  title,
  hidePlatform,
  hideToggleView,
  hideCountry,
  data,
  customFilters,
  timeIntervalFilter,
  compId,
  expandAction,
  seeAllLink,
  currentModule,
  hideDots,
  notPlatforms,
  distributed,
  notClickable,
  firstTitle = '',
  secondTitle = '',
  expandToTopCountries,
  customTooltip,
  trending_topics,
  grid,
  hideIfNoData,
  maxRows,
  additionalComponents,
  isReport,
  allCountriesFilter,
  expandUrlWithNavigate,
}) => {
  const queryClient = useQueryClient();
  const { theme } = useAppSettings();
  const { data: allCountries, isLoading: AllCountriesIsLoading } = useQuery(
    'AllCountries',
    loadAllCountries,
    {
      enabled: !!allCountriesFilter,
    }
  );

  const countriesOptions = allCountriesFilter
    ? allCountries
    : queryClient.getQueryData('getCountries');
  const platforms = queryClient.getQueryData('getPlatforms') as any;
  const [tableView, settableView] = useState<'table' | 'graph'>('table');
  const time_interval = [
    // { label: _t('basic:all_time'), value: 'All Time' },
    { label: _t('basic:day'), value: 'DAY' },
    { label: _t('basic:week'), value: 'WEEK' },
    { label: _t('basic:month'), value: 'MONTH' },
    { label: _t('basic:year'), value: 'YEAR' },
  ];
  const graphDataRef = useRef<TGD>({ categories: [], series: [] });
  const defaultCountriesIds = countriesOptions
    ?.filter((option: { default: any }) => option?.default)
    ?.map((i: any) => i.value);
  const [filters, setFilters] = useState({
    ...customFilters,
    limit: 5,
    country_ids: hideCountry || hideFilters ? null : defaultCountriesIds,
  });

  useEffect(()=>{
    setFilters((prev: any)=>({...prev,...customFilters}))
  },[customFilters])
  
  const {
    isLoading,
    data: QData,
    isError,
  } = useQuery(
    ['loadSecondaryTableData', { body: filters, slug: moduleName }],
    loadSecondaryTableData,
    {
      enabled: !!moduleName,
      staleTime: 0,

      cacheTime: 0,
    }
  );
  const tableData = !!moduleName ? QData : data;
  const transTableData = tableData?.map((item: { name: any }) => ({
    ...item,
    // name: handelNameKey(item.name),
    name: item?.name,
  }));

  const handleChangeFilters = (name: string) => (option: any) => {
    if (Array.isArray(option)) {
      const values = option.map((item: any) => Number(item.value));
      setFilters((prevFilters: any) => ({ ...prevFilters, [name]: values }));
    } else {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [name]: option.value,
      }));
    }
  };

  const renderTopBar = () => (
    <div className={styles.SecondaryTable__TopBar} id={`${compId}-sT-topBar`}>
      {title ? (
        <p className={styles.Date}>{title}</p>
      ) : (
        <>
          <div className={styles.FiltersWrapper}>
            {!hideFilters && (
              <div className={styles.Filters}>
                {!hideCountry && (
                  <div className={styles.SelectInput}>
                    <SelectInput
                      icon={<CountryIcon />}
                      placeholder={_t('trends:Country')}
                      options={countriesOptions}
                      isMulti
                      showDefaultValue
                      onChange={handleChangeFilters('country_ids')}
                      name={''}
                    />
                  </div>
                )}
                {!hidePlatform && (
                  <div className={styles.SelectInput}>
                    <SelectInput
                      icon={<PlatformIcon />}
                      placeholder={_t('trends:Platforms')}
                      options={platforms?.platformsOptions}
                      isMulti
                      onChange={handleChangeFilters('platform_ids')}
                      name={''}
                    />
                  </div>
                )}
                {timeIntervalFilter && (
                  <div
                    className={styles.SelectInput}
                    data-testid="time-interval"
                  >
                    <SelectInput
                      icon={<DateIcon />}
                      placeholder={_t('basic:day')}
                      options={time_interval}
                      onChange={handleChangeFilters('time_interval')}
                      name={''}
                    />
                  </div>
                )}
              </div>
            )}
            {tableView == 'graph' && !notPlatforms && (
              <div className={styles.Legend}>
                {graphDataRef?.current?.series?.map((val: any, i: any) => {
                  return (
                    <div key={i} className={styles.Legend__Item}>
                      <div
                        className={styles.Legend__ColorBox}
                        style={{
                          borderColor: theme.colors.chartsColors[i],
                          backgroundColor: theme.colors.chartsColors[i],
                        }}
                      ></div>
                      <p>{handelNameKey(val.name)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.Controls}>
            {!hideToggleView && (
              <div className={styles.ControlsItem}>
                {tableView == 'table' ? (
                  <div
                    onClick={() => {
                      settableView('graph');
                    }}
                  >
                    <GraphIcon />
                  </div>
                ) : (
                  <div
                    onClick={() => {
                      settableView('table');
                    }}
                  >
                    <TableIcon />
                  </div>
                )}
              </div>
            )}
            {!isReport && (
              <div className={styles.ControlsItem}>
                {expandAction ? (
                  <ExpandIcon onClick={expandAction} />
                ) : expandUrl ? (
                  <Link
                    data-testid="expandUrl"
                    className={styles.ActionBtn}
                    to={
                      expandToTopCountries
                        ? navigateWithSearchState(expandUrl as string, {
                            currentModule: currentModule && currentModule,
                            data: transTableData && transTableData,
                          })
                        : expandUrlWithNavigate
                        ? navigateWithSearchState(
                            (expandUrl as any)?.pathname as string,
                            (expandUrl as any)?.state as any
                          )
                        : expandUrl
                    }
                  >
                    <ExpandIcon />
                  </Link>
                ) : null}
              </div>
            )}
            {!hideDots && (
              <div className={styles.controlsItem}>
                {!isReport && <DotsMenu id={`${compId}-secTablePng`} />}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
  const { pathname } = useLocation();
  const renderTableCell = (row: any, column: any) => {
    //-----handel arabic when download reports
    row = pathname?.includes('report')
      ? {
          ...row,
          content:
            row?.content?.length > 0
              ? `${row?.content?.slice(0, 35)}...`
              : row?.content,
          content_translated:
            row?.content_translated?.length > 0
              ? `${row?.content_translated?.slice(0, 35)}...`
              : row?.content_translated,
          title:
            row?.title?.length > 0
              ? `${row?.title?.slice(0, 35)}...`
              : row?.title,
          title_translated:
            row?.title_translated?.length > 0
              ? `${row?.title_translated?.slice(0, 35)}...`
              : row?.title_translated,
        }
      : row;
    //-----
    return (
      <React.Fragment key={uuid()}>
        <TableCell
          key={uuid()}
          className={styles.tableCell}
          classes={{ root: styles.noBorder }}
          align={direction}
        >
          {notClickable ? (
            <span
              style={{
                pointerEvents: 'none',
              }}
            >
              {column?.render
                ? column?.render(row?.[column?.key], row)
                : row?.[column?.key]}
            </span>
          ) : column?.render ? (
            <div id="secondaryTableRow" className="tableRow">
              {column?.render(row?.[column?.key], row)}
            </div>
          ) : (
            <>
              <Link
                to={
                  (expandUrl as any)?.pathname?.includes('influencers')
                    ? path.influencers.ANALYZED_INFLUENCER_DETAILS_URI +
                      '/' +
                      row?.id
                    : path.entities.ANALYZED_ENTITIES_DETAILS_URI +
                      '/' +
                      row?.id
                }
              >
                {row?.[column?.key]}
              </Link>
            </>
          )}
        </TableCell>
      </React.Fragment>
    );
  };
  const renderGraphData = () => {
    const graphData: any = stackedChartTransformer(
      transTableData,
      platforms?.platforms,
      notPlatforms
    );
    graphDataRef.current = graphData;
    return (
      <StackedColumnChart
        distributed={distributed}
        hideCountryFilter
        hideDotsMenu
        hideTimeIntervalDotsMenu
        columnHeader={[_t(`${firstTitle}`), _t(`${secondTitle}`)]}
        hidePlatformFilter
        legendPosition={'top'}
        showToolTip={notPlatforms ? undefined : true}
        customTooltip={customTooltip}
        trending_topics={trending_topics}
        compId={`${compId}-stackedColumn`}
        data={{
          xaxis: graphData?.categories,
          values: graphData?.series,
        }}
        withSecondaryTable
      />
    );
  };

  if (
    (!transTableData?.length && hideIfNoData && !isLoading) ||
    AllCountriesIsLoading
  )
    return <></>;
  return (
    <ServerError requiredApiCalls={[isError]}>
      <Grid id={compId} className={styles.SecondaryTable} item {...grid}>
        {tableCardTitle && (
          <CardTitle
            title={tableCardTitle}
            compId={compId}
            hideIcon={isReport}
          />
        )}
        <div
          className={styles.SecondaryTable__TableContent}
          id={`${compId}-sT-content`}
        >
          {renderTopBar()}
          {!isLoading ? (
            !transTableData?.length ? (
              <NoData />
            ) : tableView == 'graph' ? (
              renderGraphData()
            ) : (
              <div className={styles.SecondaryTable__Container}>
                <Table
                  data-testid="secondary-table"
                  aria-label="simple table"
                  id={`${compId}-sT-table`}
                >
                  <TableHead>
                    <TableRow classes={{ root: styles.tableRow }}>
                      {rowAction && (
                        <TableCell size="small" padding="none"></TableCell>
                      )}
                      {columns?.map((column: any, index) => (
                        <TableCell
                          className={styles.tableCell}
                          style={column.columnStyle}
                          key={index}
                          align={direction}
                        >
                          {_t(column?.title)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {maxRows
                      ? transTableData
                          ?.slice(0, maxRows)
                          .map((row: any, i: number) => {
                            return (
                              <TableRow
                                key={row.id + '' + i}
                                classes={{
                                  root: styles.SecondaryTable__TableRow,
                                }}
                              >
                                {rowAction && (
                                  <TableCell
                                    className={
                                      styles.SecondaryTable__TableRow__AddCell
                                    }
                                    classes={{ root: styles.noBorder }}
                                    size="small"
                                    padding="none"
                                    onClick={() => rowAction(row?.id, row)}
                                  >
                                    <AddIcon />
                                  </TableCell>
                                )}
                                {columns?.map((column: any) =>
                                  renderTableCell(row, column)
                                )}
                              </TableRow>
                            );
                          })
                      : transTableData.map((row: any, i: number) => {
                          return (
                            <TableRow
                              key={row.id + '' + i}
                              classes={{
                                root: styles.SecondaryTable__TableRow,
                              }}
                            >
                              {rowAction && (
                                <TableCell
                                  className={
                                    styles.SecondaryTable__TableRow__AddCell
                                  }
                                  classes={{ root: styles.noBorder }}
                                  size="small"
                                  padding="none"
                                  onClick={() => rowAction(row?.id, row)}
                                >
                                  <AddIcon />
                                </TableCell>
                              )}
                              {columns?.map((column: any) =>
                                renderTableCell(row, column)
                              )}
                            </TableRow>
                          );
                        })}
                  </TableBody>
                </Table>
                {seeAllLink && (
                  <div className={styles.SecondaryTable__SeeAll}>
                    <Link
                      data-testid="see-all-link"
                      to={{
                        pathname: seeAllLink,
                        state: {
                          currentModule: currentModule && currentModule,
                          data: transTableData && transTableData,
                        },
                      }}
                    >
                      {_t('hashtags:See_all')}
                    </Link>
                  </div>
                )}
              </div>
            )
          ) : (
            <Loader />
          )}
        </div>
        {additionalComponents}
      </Grid>
    </ServerError>
  );
};
export default SecondaryTable;
